import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Pencil, Check, X, Bold, Italic, List, ListOrdered, AArrowUp, AArrowDown } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ToolbarBtn = ({ active, onClick, children, title }) => (
  <button type="button" onMouseDown={(e) => { e.preventDefault(); onClick(); }} title={title}
    className={`p-1.5 rounded transition-colors ${active ? "bg-slate-200 text-slate-800" : "text-slate-400 hover:text-slate-600 hover:bg-slate-100"}`}>
    {children}
  </button>
);

export const OverviewPanel = () => {
  const { selectedProduct } = useAuth();
  const productId = selectedProduct?.id;
  const [goalHtml, setGoalHtml] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const editorRef = useRef(null);

  const fetchGoal = useCallback(async () => {
    try { 
      const params = productId ? { product_id: productId } : {};
      const r = await axios.get(`${API}/project-goal`, { params }); 
      setGoalHtml(r.data.text); 
    } catch (e) { console.error(e); }
  }, [productId]);

  useEffect(() => { fetchGoal(); }, [fetchGoal]);

  const startEdit = () => {
    setEditing(true);
    setTimeout(() => { if (editorRef.current) { editorRef.current.innerHTML = goalHtml; editorRef.current.focus(); } }, 50);
  };

  const cancelEdit = () => { setEditing(false); };

  const saveGoal = async () => {
    setSaving(true);
    const html = editorRef.current?.innerHTML || "";
    try { await axios.put(`${API}/project-goal`, { text: html, product_id: productId }); setGoalHtml(html); setEditing(false); } catch (e) { console.error(e); }
    setSaving(false);
  };

  const exec = (cmd, val) => { document.execCommand(cmd, false, val); editorRef.current?.focus(); };

  return (
    <div data-testid="overview-panel">
      <p className="text-xs font-semibold tracking-widest uppercase text-[#00D4C8] mb-3">Product Overview</p>
      <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 dark:text-slate-100 font-['Outfit'] leading-tight" data-testid="overview-title">{selectedProduct?.name || "Product Builder"}</h1>
      <p className="text-base sm:text-lg text-slate-500 dark:text-slate-400 mt-2 font-medium">{selectedProduct?.description || "Product Lifecycle Management"}</p>
      <Separator className="my-8 bg-slate-200 dark:bg-slate-700" />

      <Card className="border border-slate-200 dark:border-slate-700 shadow-sm bg-white dark:bg-slate-800/50" data-testid="overview-summary">
        <CardContent className="p-6">
          {editing ? (
            <div className="space-y-3">
              {/* Toolbar */}
              <div className="flex items-center gap-1 border border-slate-200 dark:border-slate-600 rounded-lg px-2 py-1 bg-slate-50 dark:bg-slate-700" data-testid="editor-toolbar">
                <ToolbarBtn onClick={() => exec("bold")} title="Bold"><Bold className="w-4 h-4" /></ToolbarBtn>
                <ToolbarBtn onClick={() => exec("italic")} title="Italic"><Italic className="w-4 h-4" /></ToolbarBtn>
                <div className="w-px h-5 bg-slate-200 mx-1" />
                <ToolbarBtn onClick={() => exec("insertUnorderedList")} title="Bullet List"><List className="w-4 h-4" /></ToolbarBtn>
                <ToolbarBtn onClick={() => exec("insertOrderedList")} title="Numbered List"><ListOrdered className="w-4 h-4" /></ToolbarBtn>
                <div className="w-px h-5 bg-slate-200 mx-1" />
                <ToolbarBtn onClick={() => exec("fontSize", "3")} title="Small"><span className="text-[10px] font-bold">A</span></ToolbarBtn>
                <ToolbarBtn onClick={() => exec("fontSize", "4")} title="Normal"><span className="text-xs font-bold">A</span></ToolbarBtn>
                <ToolbarBtn onClick={() => exec("fontSize", "5")} title="Large"><span className="text-sm font-bold">A</span></ToolbarBtn>
                <ToolbarBtn onClick={() => exec("fontSize", "6")} title="Extra Large"><span className="text-base font-bold">A</span></ToolbarBtn>
              </div>
              {/* Editable area */}
              <div ref={editorRef} contentEditable suppressContentEditableWarning data-testid="project-goal-editor"
                className="min-h-[200px] p-4 border border-slate-200 dark:border-slate-600 rounded-lg text-sm text-slate-700 dark:text-slate-200 leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#00D4C8]/30 focus:border-[#00D4C8] prose prose-sm dark:prose-invert max-w-none
                  [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2
                  [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2
                  [&_li]:my-0.5
                  [&_b]:font-bold [&_strong]:font-bold
                  [&_i]:italic [&_em]:italic" />
              <div className="flex gap-2">
                <Button onClick={saveGoal} disabled={saving} size="sm" className="bg-[#0F172A] hover:bg-slate-800 text-white" data-testid="project-goal-save-btn">
                  <Check className="w-4 h-4 mr-1" />{saving ? "Saving..." : "Save"}
                </Button>
                <Button onClick={cancelEdit} variant="outline" size="sm" data-testid="project-goal-cancel-btn"><X className="w-4 h-4 mr-1" />Cancel</Button>
              </div>
            </div>
          ) : (
            <div className="relative group">
              <button onClick={startEdit} className="absolute top-0 right-0 p-1.5 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all opacity-0 group-hover:opacity-100" data-testid="project-goal-edit-btn">
                <Pencil className="w-4 h-4" />
              </button>
              {goalHtml ? (
                <div className="text-sm text-slate-600 dark:text-white leading-relaxed prose prose-sm dark:prose-invert max-w-none
                  [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:my-2
                  [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:my-2
                  [&_li]:my-0.5" data-testid="project-goal-text" dangerouslySetInnerHTML={{ __html: goalHtml }} />
              ) : (
                <p className="text-sm text-slate-400 italic cursor-pointer" onClick={startEdit} data-testid="project-goal-empty">Click to add project overview content...</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
